import { hashKey, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

const controllers = new Map<string, AbortController>();

/**
 * Runs a dashboard request with its own abort controller instead of the query's mount signal,
 * so an in-flight request survives StrictMode remounts and is only aborted once superseded.
 */
export function supersedableDashboardQueryFn<T>(
  queryKey: QueryKey,
  run: (signal: AbortSignal) => Promise<T>,
) {
  return () => {
    const hash = hashKey(queryKey);
    const controller = new AbortController();
    controllers.set(hash, controller);
    return run(controller.signal).finally(() => {
      if (controllers.get(hash) === controller) controllers.delete(hash);
    });
  };
}

/**
 * Aborts in-flight requests for query keys this screen stopped using (a changed date range)
 * once nothing else observes them. The query is cancelled first so it reverts instead of
 * retrying or reporting the abort; revisiting that range fetches it again.
 */
export function useAbortSupersededDashboardQueries(queryKeys: readonly QueryKey[]) {
  const queryClient = useQueryClient();
  const previousKeys = useRef<readonly QueryKey[]>([]);
  const signature = queryKeys.map((queryKey) => hashKey(queryKey)).join('|');

  useEffect(() => {
    const currentHashes = new Set(signature.split('|'));
    for (const queryKey of previousKeys.current) {
      const hash = hashKey(queryKey);
      const controller = controllers.get(hash);
      if (currentHashes.has(hash) || !controller) continue;
      const query = queryClient.getQueryCache().find({ queryKey, exact: true });
      if (query && query.getObserversCount() > 0) continue;
      void queryClient.cancelQueries({ queryKey, exact: true });
      controller.abort();
      controllers.delete(hash);
    }
    previousKeys.current = queryKeys;
    // queryKeys is represented by its hash signature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, signature]);
}
